import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { IoInformationCircleOutline } from 'react-icons/io5';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useAuth } from '../../../context/AuthContext';

const SECTIONS = [
  {
    title: '상담요청 알림',
    items: [
      { key: 'newReg', label: '신규 등록 알림', desc: '상담요청 DB 신규 등록 시 푸시 알림을 받습니다.', defaultOn: true },
    ],
  },
  {
    title: '리워드',
    items: [
      { key: 'rewardPay', label: '마케팅 리워드 지급 알림', desc: '리워드 수익금이 지급되면 푸시 알림을 받습니다.', defaultOn: true },
      { key: 'rewardWithdraw', label: '마케팅 리워드 출금가능일 알림', desc: '출금 가능일이 되면 푸시 알림을 받습니다.', defaultOn: false },
    ],
  },
  {
    title: '출금신청',
    items: [
      { key: 'withdrawReq', label: '출금신청 알림', desc: '출금 신청이 접수되면 푸시 알림을 받습니다.', defaultOn: true },
      { key: 'withdrawDone', label: '출금완료 알림', desc: '출금이 완료되면 푸시 알림을 받습니다.', defaultOn: false },
    ],
  },
  {
    title: '기타',
    items: [
      { key: 'notice', label: '공지 알림', desc: '새 공지가 등록되면 푸시 알림을 받습니다.', defaultOn: true },
      { key: 'login', label: '로그인 알림', desc: '새 기기에서 로그인 시 푸시 알림을 받습니다.', defaultOn: false },
      { key: 'infoChange', label: '회원정보 변경 알림', desc: '회원정보가 변경되면 푸시 알림을 받습니다.', defaultOn: true },
    ],
  },
];

const buildDefault = () => {
  const obj = {};
  SECTIONS.forEach((s) => s.items.forEach((i) => { obj[i.key] = i.defaultOn; }));
  return obj;
};

const NotifSettings = () => {
  const { userData } = useAuth();
  const uid = userData?.uid;
  const [settings, setSettings] = useState(buildDefault);

  // Firestore에서 로드
  useEffect(() => {
    if (!uid) return;
    const ref = doc(db, 'users', uid, 'settings', 'notifications');
    getDoc(ref).then((snap) => {
      if (snap.exists()) {
        setSettings((prev) => ({ ...prev, ...snap.data() }));
      }
    }).catch(() => {});
  }, [uid]);

  // 토글 시 Firestore 저장
  const toggle = (key) => {
    const newVal = !settings[key];
    setSettings((prev) => ({ ...prev, [key]: newVal }));
    if (uid) {
      const ref = doc(db, 'users', uid, 'settings', 'notifications');
      setDoc(ref, { [key]: newVal }, { merge: true }).catch(() => {});
    }
  };

  return (
    <Container>
      {SECTIONS.map((section) => (
        <Section key={section.title}>
          <SectionLabel>{section.title}</SectionLabel>
          <Card>
            {section.items.map((item, idx) => (
              <NotifRow key={item.key} $last={idx === section.items.length - 1}>
                <NotifContent>
                  <NotifLabel>{item.label}</NotifLabel>
                  <NotifDesc>{item.desc}</NotifDesc>
                </NotifContent>
                <ToggleWrapper onClick={() => toggle(item.key)}>
                  <ToggleTrack $on={settings[item.key]}>
                    <ToggleThumb $on={settings[item.key]} />
                  </ToggleTrack>
                </ToggleWrapper>
              </NotifRow>
            ))}
          </Card>
        </Section>
      ))}

      <FooterText>
        <IoInformationCircleOutline size={16} style={{ flexShrink: 0, marginTop: 2 }} />
        <span>
          푸시 알림을 받으려면 기기 설정에서 이음 앱 알림을 허용해 주세요.
          알림을 거부하셨다면 설정 &gt; 이음 &gt; 알림에서 나중에 허용할 수 있으며,
          앱은 알림 없이도 정상 이용 가능합니다.
        </span>
      </FooterText>
    </Container>
  );
};

export default NotifSettings;

const Container = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing.lg};
  background: #F3F4F6;
  margin: -${({ theme }) => theme.realtor.pagePadding};
  padding: ${({ theme }) => theme.realtor.pagePadding};
  min-height: 100vh;
`;

const Section = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const SectionLabel = styled.p`
  font-size: 12px;
  color: #4B5563;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.bgWhite};
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const NotifRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: 12px 14px;
  border-bottom: ${({ $last, theme }) => ($last ? 'none' : `1px solid ${theme.colors.borderLight}`)};
`;

const NotifContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const NotifLabel = styled.div`
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: #1F2937;
  margin-bottom: 2px;
`;

const NotifDesc = styled.div`
  font-size: 11px;
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  line-height: 1.3;
`;

const ToggleWrapper = styled.div`
  flex-shrink: 0;
  cursor: pointer;
`;

const ToggleTrack = styled.div`
  width: 36px;
  height: 20px;
  border-radius: 10px;
  position: relative;
  background: ${({ $on, theme }) => ($on ? theme.colors.primary : '#D1D5DB')};
  transition: background 0.2s;
`;

const ToggleThumb = styled.div`
  position: absolute;
  top: -1px;
  width: 22px;
  height: 22px;
  border-radius: ${({ theme }) => theme.radius.full};
  background: #fff;
  left: ${({ $on }) => ($on ? '16px' : '-2px')};
  transition: left 0.2s;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
`;

const FooterText = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 13px;
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  line-height: 1.5;
  margin-top: ${({ theme }) => theme.spacing.base};
`;
